import { isProduction } from "~/helpers/environment";

// Google, Bing, Meta and OpenAI ad click IDs.
export const CLICK_ID_KEYS = [
  "gclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "fbclid",
  "oppref",
] as const;
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number];
export const CLICK_ID_TTL_DAYS = 90;

// Stored with the click IDs and dropped with them: when and where the touch happened
// (the marketing site passes these on), and which of its buttons led to signup.
export const TOUCH_CONTEXT_KEYS = [
  "touch_ts",
  "landing_url",
  "signup_cta",
] as const;
export type TouchContextKey = (typeof TOUCH_CONTEXT_KEYS)[number];

type ClickIds = Record<ClickIdKey, string | null>;
type StoredKey = ClickIdKey | TouchContextKey;

const expiresKey = (key: StoredKey) => `${key}_expires`;

// A landing carrying any of these is a new ad touch and replaces the whole stored
// touch (last click). utm_content or other params on their own are not a touch, and
// neither is a partner ID (ps_xid, irclickid) alone: a partner link must not wipe
// the ad click it came with.
const UTM_TOUCH_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_campaign_name",
];

// The marketing site's own buttons link here with utm_medium=organic (e.g.
// utm_source=homepage). Without a click ID that is not an ad touch, so it must not
// replace a stored one.
const INTERNAL_MEDIUMS = ["organic"];

export const isNewTouch = (params: URLSearchParams): boolean => {
  if (CLICK_ID_KEYS.some((key) => !!params.get(key))) return true;
  const medium = (params.get("utm_medium") || "").trim().toLowerCase();
  if (INTERNAL_MEDIUMS.includes(medium)) return false;
  return UTM_TOUCH_PARAM_KEYS.some((key) => !!params.get(key));
};

// Values the marketing team uses to test ads; such a landing is ignored whole.
const QA_VALUES: Record<string, string[]> = {
  utm_campaign: ["qa111", "qb111", "qb444"],
  utm_campaign_name: ["qa111", "qb111", "qb444"],
  utm_source: ["test"],
  gclid: ["qa_gclid_test", "test1"],
  msclkid: ["qb111", "qb444"],
  qa: ["yes"],
};

/** A QA test landing. Only filtered in production, so staging can test end to end. */
export const isQaTouch = (
  params: URLSearchParams,
  enabled: boolean = isProduction
): boolean =>
  enabled &&
  Object.entries(QA_VALUES).some(([key, values]) =>
    values.includes((params.get(key) || "").trim().toLowerCase())
  );

const getLocalStorage = () => {
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      return window.localStorage;
    }
    if (typeof globalThis !== "undefined" && (globalThis as any).localStorage) {
      return (globalThis as any).localStorage;
    }
  } catch {
    return null;
  }
  return null;
};

const safeGetItem = (key: string): string | null => {
  try {
    const storage = getLocalStorage();
    return typeof storage?.getItem === "function" ? storage.getItem(key) : null;
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string) => {
  try {
    const storage = getLocalStorage();
    if (typeof storage?.setItem === "function") {
      storage.setItem(key, value);
    }
  } catch {
    return;
  }
};

const safeRemoveItem = (key: string) => {
  try {
    const storage = getLocalStorage();
    if (typeof storage?.removeItem === "function") {
      storage.removeItem(key);
    }
  } catch {
    return;
  }
};

const getCookieDomain = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  const { hostname } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1") return undefined;

  // app.dropshiptool.io / www.dropshiptool.io → .dropshiptool.io
  const parts = hostname.split(".");
  if (parts.length >= 2) {
    return `.${parts.slice(-2).join(".")}`;
  }
  return undefined;
};

const setCookie = (name: string, value: string, days: number) => {
  if (typeof document === "undefined") return;
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    const domain = getCookieDomain();
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
    if (domain) cookie += `; domain=${domain}`;
    if (window.location.protocol === "https:") cookie += "; Secure";
    document.cookie = cookie;
  } catch {
    return;
  }
};

const getCookie = (name: string): string | null => {
  if (typeof document === "undefined") return null;
  try {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${encodeURIComponent(name)}=([^;]*)`)
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
};

const isClickIdKey = (key: StoredKey): key is ClickIdKey =>
  (CLICK_ID_KEYS as readonly string[]).includes(key);

// Click IDs are also kept in a cookie for the marketing site → app hop; the touch
// context stays in localStorage only, so it is not sent with every request.
const persistClickId = (key: StoredKey, value: string) => {
  const expiresAt = Date.now() + CLICK_ID_TTL_DAYS * 864e5;
  safeSetItem(key, value);
  safeSetItem(expiresKey(key), String(expiresAt));
  if (isClickIdKey(key)) setCookie(key, value, CLICK_ID_TTL_DAYS);
};

const clearPersistedClickIds = () => {
  for (const key of [...CLICK_ID_KEYS, ...TOUCH_CONTEXT_KEYS]) {
    safeRemoveItem(key);
    safeRemoveItem(expiresKey(key));
    // An expiry in the past deletes the cookie.
    if (isClickIdKey(key)) setCookie(key, "", -1);
  }
};

/**
 * Each click ID is used by one signup: once the account exists the API keeps them,
 * so they are dropped here and a later signup in this browser can't reuse them.
 * Call only after signup succeeded.
 */
export const clearAttributionAfterSignup = clearPersistedClickIds;

export const getPersistedClickId = (key: StoredKey): string | null => {
  const expiresRaw = safeGetItem(expiresKey(key));
  const expiresAt = expiresRaw !== null ? Number(expiresRaw) : NaN;
  const hasValidExpiry = Number.isFinite(expiresAt);

  if (hasValidExpiry) {
    if (Date.now() > expiresAt) {
      safeRemoveItem(key);
      safeRemoveItem(expiresKey(key));
    } else {
      const fromLs = safeGetItem(key);
      if (fromLs) return fromLs;
    }
  } else {
    // Missing or corrupted expiry (legacy/invalid data): reset the TTL from the
    // existing value so the click ID can still expire, otherwise it would persist
    // indefinitely.
    const fromLs = safeGetItem(key);
    if (fromLs) {
      const newExpiresAt = Date.now() + CLICK_ID_TTL_DAYS * 864e5;
      safeSetItem(expiresKey(key), String(newExpiresAt));
      if (isClickIdKey(key)) setCookie(key, fromLs, CLICK_ID_TTL_DAYS);
      return fromLs;
    }
    // No value to keep — drop the stale/invalid expiry key if present.
    if (expiresRaw !== null) safeRemoveItem(expiresKey(key));
  }

  return isClickIdKey(key) ? getCookie(key) : null;
};

export const getAllPersistedClickIds = (): ClickIds => ({
  gclid: getPersistedClickId("gclid"),
  gbraid: getPersistedClickId("gbraid"),
  wbraid: getPersistedClickId("wbraid"),
  msclkid: getPersistedClickId("msclkid"),
  fbclid: getPersistedClickId("fbclid"),
  oppref: getPersistedClickId("oppref"),
});

export const getTouchContext = (): Record<TouchContextKey, string | null> => ({
  touch_ts: getPersistedClickId("touch_ts"),
  landing_url: getPersistedClickId("landing_url"),
  signup_cta: getPersistedClickId("signup_cta"),
});

// Never stored with the touch: it could carry the visitor's email.
const PRIVATE_URL_PARAMS = ["email"];

// Drops the private params and the hash from a landing URL; anything that isn't a
// URL is not stored.
const sanitizeLandingUrl = (raw: string): string | null => {
  try {
    const url = new URL(raw);
    PRIVATE_URL_PARAMS.forEach((key) => url.searchParams.delete(key));
    url.hash = "";
    return url.toString();
  } catch {
    return null;
  }
};

const currentLandingUrl = (): string | null =>
  typeof window === "undefined"
    ? null
    : sanitizeLandingUrl(window.location.href);

// The marketing site sends touch_ts in unix seconds; anything else is not one.
const validTouchTs = (value: string | null): string | null =>
  value && /^\d{9,10}$/.test(value.trim()) ? value.trim() : null;

/**
 * Meta's browser ID (_fbp) and click ID (_fbc) cookies. Without a Meta pixel on this
 * domain _fbc is derived from the stored fbclid, in Meta's own format.
 */
export const getMetaBrowserIds = (): {
  _fbp: string | null;
  _fbc: string | null;
} => {
  const fbclid = getPersistedClickId("fbclid");
  const touchTs = Number(getPersistedClickId("touch_ts"));
  const clickMs =
    Number.isFinite(touchTs) && touchTs > 0 ? touchTs * 1000 : null;
  return {
    _fbp: getCookie("_fbp"),
    _fbc:
      getCookie("_fbc") ||
      (fbclid && clickMs ? `fb.1.${clickMs}.${fbclid}` : null),
  };
};

export const captureClickIdsFromSearch = (
  search: string | URLSearchParams = typeof window !== "undefined"
    ? window.location.search
    : ""
): Partial<ClickIds> => {
  const params =
    typeof search === "string"
      ? new URLSearchParams(search.startsWith("?") ? search : `?${search}`)
      : search;

  const captured: Partial<ClickIds> = {};
  // Last click: a new touch replaces every stored click ID, including ones it doesn't carry.
  if (!isNewTouch(params) || isQaTouch(params)) return captured;
  clearPersistedClickIds();
  for (const key of CLICK_ID_KEYS) {
    const value = params.get(key);
    if (value) {
      persistClickId(key, value);
      captured[key] = value;
    }
  }
  const context: Record<TouchContextKey, string | null> = {
    touch_ts:
      validTouchTs(params.get("touch_ts")) ||
      String(Math.floor(Date.now() / 1000)),
    landing_url:
      sanitizeLandingUrl(params.get("landing_url") || "") ||
      currentLandingUrl(),
    signup_cta: params.get("signup_cta"),
  };
  for (const [key, value] of Object.entries(context) as Array<
    [TouchContextKey, string | null]
  >) {
    if (value) persistClickId(key, value);
  }
  return captured;
};

/** Append persisted click IDs to a URL when missing (marketing → app domain hop). */
export const appendClickIdsToUrl = (url: string): string => {
  try {
    const origin =
      typeof window !== "undefined"
        ? window.location.origin
        : "https://app.dropshiptool.io";
    const parsed = new URL(url, origin);
    const clickIds = getAllPersistedClickIds();
    for (const key of CLICK_ID_KEYS) {
      const value = clickIds[key];
      if (value && !parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, value);
      }
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

/**
 * Bing-parity aliases → Dropshiptool stored keys.
 * Google ValueTrack final URLs may use either naming style.
 */
export const UTM_ALIAS_MAP: Record<string, string> = {
  utm_campaign_name: "utm_campaign",
  utm_campaign_id: "utm_campaignid",
  utm_adgroup_name: "utm_adgroup",
  utm_adgroup_id: "utm_adgroup",
  campaignid: "utm_campaignid",
  adgroupid: "utm_adgroup",
  matchtype: "utm_matchtype",
  keyword: "utm_keyword",
};

export const resolveUtmParam = (
  searchParams: URLSearchParams,
  primaryKey: string,
  ...aliasKeys: string[]
): string | null => {
  const primary = searchParams.get(primaryKey);
  if (primary) return primary;
  for (const alias of aliasKeys) {
    const value = searchParams.get(alias);
    if (value) return value;
  }
  return null;
};

export const hasClickIdAttribution = (ids: Partial<ClickIds>): boolean =>
  Object.entries(ids).some(
    ([key, value]) =>
      !!value && (CLICK_ID_KEYS as readonly string[]).includes(key)
  );
